import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, Plus, RefreshCw, Search, FileText, CheckCircle, Clock, AlertCircle, DollarSign, Eye } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface ProcedureType {
  id: number;
  code: string;
  name: string;
}

interface Alumno {
  id: number;
  fullName: string;
  nui: string;
}

interface Invoice {
  id: number;
  invoiceNumber: string;
  total: string;
  status: string;
}

interface Teacher {
  id: number;
  nombre: string;
}

interface Procedure {
  id: number;
  status: 'solicitado' | 'pendiente_pago' | 'pagado_en_proceso' | 'en_proceso' | 'completado' | 'cancelado';
  notes: string | null;
  requestedAt: string;
  completedAt: string | null;
  procedureType: ProcedureType & { targetAudience?: string };
  alumno: Alumno | null;
  teacher: Teacher | null;
  requestedBy: string;
  requestedByType: 'alumno' | 'profesor' | 'desconocido';
  invoice: Invoice | null;
  hasCost: boolean;
  productName: string | null;
  productPrice: string | null;
  productPriceWithTax: string | null;
  invoiceTotal: string | null;
}

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: any }> = {
  'solicitado': { label: 'Solicitado', variant: 'secondary', icon: Clock },
  'pendiente_pago': { label: 'Pendiente de Pago', variant: 'destructive', icon: DollarSign },
  'pagado_en_proceso': { label: 'Pagado - En Proceso', variant: 'default', icon: CheckCircle },
  'en_proceso': { label: 'En Proceso', variant: 'default', icon: Clock },
  'completado': { label: 'Completado', variant: 'outline', icon: CheckCircle },
  'cancelado': { label: 'Cancelado', variant: 'destructive', icon: AlertCircle },
};

export default function ProceduresList() {
  const { toast } = useToast();
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [procedureTypes, setProcedureTypes] = useState<ProcedureType[]>([]);
  const [alumnos, setAlumnos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState<Procedure | null>(null);
  const [formData, setFormData] = useState({ procedureTypeId: '', alumnoId: '', notes: '' });
  const [newStatus, setNewStatus] = useState<string>('');
  const [alumnoSearchTerm, setAlumnoSearchTerm] = useState('');
  const [alumnoResults, setAlumnoResults] = useState<any[]>([]);
  const [selectedAlumnoDisplay, setSelectedAlumnoDisplay] = useState<string>('');
  const [showAlumnoDropdown, setShowAlumnoDropdown] = useState(false);
  const [searchingAlumnos, setSearchingAlumnos] = useState(false);

  const fetchProcedures = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await fetch(`/api/procedures?${params}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setProcedures(data);
      }
    } catch (error) {
      console.error('Error fetching procedures:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProcedureTypes = async () => {
    try {
      const response = await fetch('/api/procedures/types', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setProcedureTypes(data.filter((t: any) => t.isActive));
      }
    } catch (error) {
      console.error('Error fetching procedure types:', error);
    }
  };

  const alumnoInputRef = useRef<HTMLInputElement>(null);
  const savedAlumnoCursor = useRef<{ start: number; end: number } | null>(null);

  const searchAlumnos = async (search: string) => {
    const el = alumnoInputRef.current;
    savedAlumnoCursor.current = el
      ? { start: el.selectionStart ?? 0, end: el.selectionEnd ?? 0 }
      : null;
    try {
      setSearchingAlumnos(true);
      const response = await fetch(`/api/alumnos?search=${encodeURIComponent(search)}&limit=15`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAlumnoResults(data.alumnos || []);
        setShowAlumnoDropdown(true);
      }
    } catch (error) {
      console.error('Error searching alumnos:', error);
    } finally {
      setSearchingAlumnos(false);
      requestAnimationFrame(() => {
        const cur = savedAlumnoCursor.current;
        if (cur && alumnoInputRef.current) {
          alumnoInputRef.current.focus();
          alumnoInputRef.current.setSelectionRange(cur.start, cur.end);
        }
        savedAlumnoCursor.current = null;
      });
    }
  };

  useEffect(() => {
    fetchProcedures();
    fetchProcedureTypes();
  }, []);

  useEffect(() => {
    if (alumnoSearchTerm.length >= 1) {
      const timer = setTimeout(() => {
        searchAlumnos(alumnoSearchTerm);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setAlumnoResults([]);
      setShowAlumnoDropdown(false);
    }
  }, [alumnoSearchTerm]);

  useEffect(() => {
    fetchProcedures();
  }, [statusFilter]);

  const handleCreate = async () => {
    if (!formData.procedureTypeId || !formData.alumnoId) {
      toast({ title: 'Error', description: 'Seleccione tipo de trámite y alumno', variant: 'destructive' });
      return;
    }

    try {
      const response = await fetch('/api/procedures', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          procedureTypeId: parseInt(formData.procedureTypeId),
          alumnoId: parseInt(formData.alumnoId),
          notes: formData.notes || undefined,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Trámite creado',
          description: result.invoiceCreated
            ? 'Se generó una cuenta de cobro para este trámite'
            : 'Trámite registrado sin costo',
        });
        setShowCreateDialog(false);
        setFormData({ procedureTypeId: '', alumnoId: '', notes: '' });
        setSelectedAlumnoDisplay('');
        setAlumnoSearchTerm('');
        setAlumnoResults([]);
        fetchProcedures();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.error || 'Error al crear trámite', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error de conexión', variant: 'destructive' });
    }
  };

  const handleStatusChange = async () => {
    if (!selectedProcedure || !newStatus) return;

    try {
      const response = await fetch(`/api/procedures/${selectedProcedure.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        toast({ title: 'Estado actualizado', description: `Trámite marcado como ${statusConfig[newStatus]?.label}` });
        setShowStatusDialog(false);
        setSelectedProcedure(null);
        setNewStatus('');
        fetchProcedures();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.error || 'Error al cambiar estado', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error de conexión', variant: 'destructive' });
    }
  };

  const getAvailableTransitions = (procedure: Procedure): string[] => {
    const { status, hasCost } = procedure;
    
    switch (status) {
      case 'solicitado':
        return hasCost ? ['pendiente_pago', 'cancelado'] : ['en_proceso', 'cancelado'];
      case 'pendiente_pago':
        return ['pagado_en_proceso', 'cancelado'];
      case 'pagado_en_proceso':
        return ['completado', 'cancelado'];
      case 'en_proceso':
        return ['completado', 'cancelado'];
      default:
        return [];
    }
  };

  const openStatusDialog = (procedure: Procedure) => {
    const transitions = getAvailableTransitions(procedure);
    if (transitions.length === 0) {
      toast({ title: 'Información', description: 'Este trámite ya está en un estado final' });
      return;
    }
    setSelectedProcedure(procedure);
    setNewStatus('');
    setShowStatusDialog(true);
  };

  const filteredProcedures = procedures.filter(p => {
    const matchesSearch =
      (p.requestedBy || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.alumno?.nui || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.procedureType.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: string) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(parseFloat(amount));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Trámites Académicos</h1>
            <p className="text-muted-foreground">Gestión de solicitudes de trámites</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchProcedures} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Trámite
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4 justify-between">
            <div className="flex gap-2 flex-1">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por alumno o trámite..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="solicitado">Solicitado</SelectItem>
                  <SelectItem value="pendiente_pago">Pendiente de Pago</SelectItem>
                  <SelectItem value="pagado_en_proceso">Pagado - En Proceso</SelectItem>
                  <SelectItem value="en_proceso">En Proceso</SelectItem>
                  <SelectItem value="completado">Completado</SelectItem>
                  <SelectItem value="cancelado">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Cargando...</div>
          ) : filteredProcedures.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm || statusFilter !== 'all' ? 'No se encontraron resultados' : 'No hay trámites registrados'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium">Fecha</th>
                    <th className="text-left py-3 px-4 font-medium">Solicitante</th>
                    <th className="text-left py-3 px-4 font-medium">Tipo</th>
                    <th className="text-left py-3 px-4 font-medium">Trámite</th>
                    <th className="text-left py-3 px-4 font-medium">Costo</th>
                    <th className="text-left py-3 px-4 font-medium">Estado</th>
                    <th className="text-left py-3 px-4 font-medium">Factura</th>
                    <th className="text-center py-3 px-4 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProcedures.map((procedure) => {
                    const statusInfo = statusConfig[procedure.status];
                    const StatusIcon = statusInfo?.icon || Clock;
                    
                    return (
                      <tr key={procedure.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4 text-sm">{formatDate(procedure.requestedAt)}</td>
                        <td className="py-3 px-4">
                          <div>
                            <div className="font-medium">{procedure.requestedBy}</div>
                            <div className="text-xs text-muted-foreground">
                              {procedure.alumno?.nui || ''}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            procedure.requestedByType === 'alumno' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                          }`}>
                            {procedure.requestedByType === 'alumno' ? 'Alumno' : 'Profesor'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <span>{procedure.procedureType.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {procedure.hasCost ? (
                            <span className="text-green-600 font-medium">
                              {formatCurrency(procedure.invoiceTotal ?? procedure.productPriceWithTax ?? procedure.productPrice ?? '0')}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">Sin costo</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={statusInfo?.variant || 'secondary'} className="gap-1">
                            <StatusIcon className="h-3 w-3" />
                            {statusInfo?.label || procedure.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {procedure.invoice ? (
                            <div className="text-sm">
                              <div className="font-mono">{procedure.invoice.invoiceNumber}</div>
                              <Badge variant={procedure.invoice.status === 'PAID' ? 'outline' : 'secondary'} className="text-xs">
                                {procedure.invoice.status === 'PAID' ? 'Pagada' : 
                                 procedure.invoice.status === 'ISSUED' ? 'Emitida' : procedure.invoice.status}
                              </Badge>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openStatusDialog(procedure)}
                              disabled={procedure.status === 'completado' || procedure.status === 'cancelado'}
                            >
                              Cambiar Estado
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo Trámite Académico</DialogTitle>
            <DialogDescription>Registrar una nueva solicitud de trámite</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo de Trámite *</Label>
              <Select
                value={formData.procedureTypeId}
                onValueChange={(val) => setFormData({ ...formData, procedureTypeId: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo de trámite" />
                </SelectTrigger>
                <SelectContent>
                  {procedureTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id.toString()}>
                      {type.code} - {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Alumno *</Label>
              {selectedAlumnoDisplay ? (
                <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">{selectedAlumnoDisplay}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, alumnoId: '' });
                      setSelectedAlumnoDisplay('');
                      setAlumnoSearchTerm('');
                      setAlumnoResults([]);
                    }}
                    className="text-gray-400 hover:text-gray-600 ml-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      ref={alumnoInputRef}
                      placeholder="Escribe nombre, apellido o NUI..."
                      value={alumnoSearchTerm}
                      onChange={(e) => setAlumnoSearchTerm(e.target.value)}
                      onFocus={() => { if (alumnoResults.length > 0) setShowAlumnoDropdown(true); }}
                      className="pl-9 pr-8"
                      autoComplete="off"
                    />
                    {searchingAlumnos && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                  {showAlumnoDropdown && alumnoSearchTerm.length >= 1 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-56 overflow-auto z-50">
                      {alumnoResults.length > 0 ? (
                        alumnoResults.map((alumno) => (
                          <button
                            key={alumno.id}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, alumnoId: alumno.id.toString() });
                              setSelectedAlumnoDisplay(`${alumno.numeroIdentificacionUnico} - ${alumno.nombre} ${alumno.apellidoPaterno} ${alumno.apellidoMaterno || ''}`);
                              setAlumnoSearchTerm('');
                              setAlumnoResults([]);
                              setShowAlumnoDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-b-0 transition"
                          >
                            <p className="font-medium text-sm">{alumno.nombre} {alumno.apellidoPaterno} {alumno.apellidoMaterno || ''}</p>
                            <p className="text-xs text-muted-foreground">NUI: {alumno.numeroIdentificacionUnico}</p>
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                          {searchingAlumnos ? 'Buscando...' : 'No se encontraron alumnos'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notas adicionales..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreate}>Crear Trámite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cambiar Estado del Trámite</DialogTitle>
            <DialogDescription>
              {selectedProcedure && (
                <>
                  <strong>{selectedProcedure.procedureType.name}</strong> - {selectedProcedure.requestedBy}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Estado Actual</Label>
              <Badge variant={statusConfig[selectedProcedure?.status || '']?.variant || 'secondary'}>
                {statusConfig[selectedProcedure?.status || '']?.label || selectedProcedure?.status}
              </Badge>
            </div>
            <div className="space-y-2">
              <Label>Nuevo Estado</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar nuevo estado" />
                </SelectTrigger>
                <SelectContent>
                  {selectedProcedure && getAvailableTransitions(selectedProcedure).map((status) => (
                    <SelectItem key={status} value={status}>
                      {statusConfig[status]?.label || status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)}>Cancelar</Button>
            <Button onClick={handleStatusChange} disabled={!newStatus}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
